import { FC, useRef, useState, useEffect } from "react";
import { WorkspacesTableRowDefinition } from "../types";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import WarningIcon from "@mui/icons-material/Warning";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Stack from "@mui/material/Stack";

interface DeleteDialogProps {
  isOpen: boolean;
  workspace: WorkspacesTableRowDefinition | undefined;
  setIsOpen: (isOpen: boolean) => void;
  onConfirmDelete: () => void;
  isDeleting?: boolean;
}

export const DeleteDialog: FC<DeleteDialogProps> = (props) => {
  const titleRef = useRef<HTMLDivElement>(null);
  const [isTitleTruncated, setIsTitleTruncated] = useState(false);

  useEffect(() => {
    const checkTruncation = () => {
      if (titleRef.current) {
        setIsTitleTruncated(
          titleRef.current.scrollWidth > titleRef.current.clientWidth
        );
      }
    };

    if (props.isOpen) {
      setTimeout(checkTruncation, 100);
      window.addEventListener("resize", checkTruncation);
    }

    return () => {
      window.removeEventListener("resize", checkTruncation);
    };
  }, [props.isOpen, props.workspace?.name]);

  return (
    <Dialog
      data-testid="workspace-delete-dialog"
      open={props.isOpen}
      onClose={() => props.setIsOpen(false)}
      maxWidth="sm"
      disableRestoreFocus
      fullWidth
    >
      <DialogTitle id="delete-dialog-title" fontSize="20px" fontWeight={300}>
        <Box
          ref={titleRef}
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
          width="94%"
          py={2}
        >
          Delete Project:{" "}
          <Tooltip
            arrow
            title={props.workspace?.name || ""}
            disableHoverListener={!isTitleTruncated}
          >
            <span style={{ fontWeight: "bold" }}>
              {props.workspace?.name || ""}
            </span>
          </Tooltip>
        </Box>
      </DialogTitle>
      <IconButton
        data-testid="workspace-delete-dialog-close-btn"
        onClick={() => props.setIsOpen(false)}
        size="small"
        sx={{
          position: "absolute",
          right: 12,
          top: 26,
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
      <Divider />
      <DialogContent>
        <Stack spacing={2} py={6}>
          <Box display="flex" alignItems="center" justifyContent="center">
            <Box
              bgcolor="rgba(244, 67, 54, 0.1)"
              borderRadius="50%"
              display="flex"
              alignItems="center"
              justifyContent="center"
              width="64px"
              height="64px"
            >
              <WarningIcon sx={{ color: "#ef5350", fontSize: "32px" }} />
            </Box>
          </Box>
          <Box id="delete-dialog-description" textAlign="center">
            <Stack spacing={2}>
              <Typography fontSize="20px">Delete Project</Typography>
              <Typography fontSize="14px">
                You are going to delete the project &quot;
                {props.workspace?.name}&quot;. Are you sure?
              </Typography>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 3 }}>
        <Button
          data-testid="workspace-delete-dialog-cancel-btn"
          onClick={() => props.setIsOpen(false)}
          variant="outlined"
          color="secondary"
          sx={{ textTransform: "uppercase", minWidth: "100px" }}
        >
          Cancel
        </Button>
        <Button
          data-testid="workspace-delete-dialog-confirm-btn"
          onClick={props.onConfirmDelete}
          variant="contained"
          sx={{
            textTransform: "uppercase",
            minWidth: "100px",
            bgcolor: "#ef6c00",
            "&:hover": {
              bgcolor: "#e65100",
            },
          }}
          disabled={props.isDeleting}
        >
          Yes, Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};
